/**
 * How far along the boot really is, as a bar position.
 *
 * Engine-free and pure so the contract below can be tested without booting a game.
 *
 * The bar has one job the player can check: it must not claim to be nearly finished
 * while most of the wait is still ahead. It failed that. The old target carried an
 * unconditional `+0.25` per 90 ms tick on top of the real signal, which walked the bar
 * from 38 to the top in about twenty seconds whether or not anything had loaded — so on
 * any slow connection it reached "almost ready" on a timer and then sat there for the
 * entire real download. Liveness was bought with honesty, which is the wrong trade for
 * the one widget whose whole purpose is to tell the truth about waiting.
 *
 * There is no creep now. Both inputs move continuously on their own — the film reports
 * bytes as they arrive and the scene reports outstanding items — and the inline animation
 * in index.html eases toward whatever it is given, so the motion stays smooth without
 * anything having to be invented here.
 */

/**
 * Where index.html's label switches to "almost ready".
 *
 * Mirrored from the `phase()` thresholds in index.html, which owns the wording because
 * the label is derived from the bar's DRAWN position. Nothing here sets the label; this
 * constant exists so the promise that band makes can be asserted in a test.
 */
export const ALMOST_READY_AT = 93;

/** Where the bar starts once the engine exists, and the ceiling before "ready" is real. */
export const BOOT_FLOOR = 40;
export const BOOT_CEILING = 96;

/**
 * Assets are the bigger share, but the film is one 6.8 MB request and routinely the
 * slower of the two, so it carries enough of the bar to stay visible while it streams.
 */
const ASSET_WEIGHT = 0.55;
const FILM_WEIGHT = 0.45;

/**
 * `settled` and `filmFraction` are each 0..1 — the proportion of that stream complete.
 *
 * Weighted so the bar cannot reach the end while either is outstanding, which is what
 * makes the final band mean what it says: reaching `ALMOST_READY_AT` needs about 95% of
 * the weighted work done, and `BOOT_CEILING` needs all of it.
 */
export function bootTarget(settled: number, filmFraction: number): number {
  const s = Math.min(1, Math.max(0, settled));
  const f = Math.min(1, Math.max(0, filmFraction));
  const span = BOOT_CEILING - BOOT_FLOOR;
  return BOOT_FLOOR + (s * ASSET_WEIGHT + f * FILM_WEIGHT) * span;
}
